import { BID_LOADS_PAGE_SIZE, fetchBoxTruckBidLoads, fetchBoxTruckMyBids, MY_BIDS_PAGE_SIZE } from '../services/boxTruckLoadsApi'
import { loadKey } from '../utils/loadFields'
import type { BoxTruckBidFeedSource } from './useBoxTruckBidFeed'

/**
 * Driver Bids: every load in the project a driver/carrier has bid on, from `load_bids` on the server — not
 * just the loads already scrolled onto the operator's screen, which is what the old client-side filter over
 * the visible page did.
 *
 * The picked drivers and the equipment labels narrow it SERVER-side. The tab paginates, so a client-side
 * sift would only ever filter the rows already fetched.
 */
export const driverBidFeedSource = (driverIds: string[], equipmentLabels: string[]): BoxTruckBidFeedSource => ({
    // The filters are part of the question, so they belong in the key — a change refetches from page 1.
    key: `driver_bids:${driverIds.join(',')}:${equipmentLabels.join(',')}`,
    pageSize: BID_LOADS_PAGE_SIZE,
    label: 'driver_bids',
    // A driver bidding on a load nobody has bid on yet makes a NEW row here. A bid email never does — it
    // only tints the Contact icon of a row already shown.
    rowsAddedBy: { driverBid: true, bidEmailFrom: null },
    // No pickup-window bound: the tab always shows every bid the application's drivers placed. The endpoint
    // accepts one, and the day this board wants it the argument goes here rather than through the hook.
    fetchPage: ({ offset }) =>
        fetchBoxTruckBidLoads(
            undefined,
            undefined,
            offset,
            BID_LOADS_PAGE_SIZE,
            driverIds.length ? driverIds : undefined,
            equipmentLabels.length ? equipmentLabels : undefined
        ),
})

/**
 * My Biddings: every load the signed-in dispatcher has sent a bid email about, from `interactions`.
 *
 * Unfiltered — the endpoint takes no lane, driver or equipment narrowing — so the board's find field is its
 * only refinement. Whose bids is decided by the token, not by anything sent from here.
 */
export const myBidFeedSource = (callerUserId: string | undefined): BoxTruckBidFeedSource => ({
    key: 'my_bids',
    pageSize: MY_BIDS_PAGE_SIZE,
    label: 'my_bids',
    // This feed IS this operator's sends, so one of their own sends adds a row — and only theirs: the send
    // event fans out to the whole desk. A driver's bid changes a row's Bids column but never which loads the
    // operator emailed.
    //
    // The id is the client's own account id, used ONLY to decide whether to refetch. It is never sent: what
    // the list contains is resolved server-side from the token, because the two ids can differ (see the
    // endpoint). If they differ here the board just does not refetch on a send, which is the same staleness
    // as it not being open yet.
    //
    // That leaves a known gap, narrow and one-directional: for the cohort whose account id is not the
    // token's, a send from ANOTHER browser tab does not refresh this one — opening the board, or Refresh,
    // still does. Closing it needs the token's own `userId` on the client, which the admin never decodes;
    // the id would have to come back from this endpoint's answer and be threaded through the shared feed for
    // one of its two sources. Documented rather than built, because the failure is staleness and the
    // alternative was refetching on every teammate's send.
    rowsAddedBy: { driverBid: false, bidEmailFrom: callerUserId ?? null },
    fetchPage: ({ page }) =>
        fetchBoxTruckMyBids(page, MY_BIDS_PAGE_SIZE).then(({ bids, total }) => ({
            loads: bids.map((bid) => bid.load),
            total,
            // Each row IS a thread, so the Contact column needs no overlay call of its own.
            threads: Object.fromEntries(bids.map((bid) => [loadKey(bid.load), { emailThreadId: bid.emailThreadId, unreadCount: bid.unreadCount }])),
        })),
})
